import { useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  getStoredToken,
  getStoredRefreshToken,
  refreshAccessToken,
} from '../api/users/refresh-token';

// Refresh this many seconds before the token's `exp`.
const DEFAULT_BUFFER_SECONDS = 60;
// Never schedule tighter than this — a short-lived token (e.g. 2m in dev) must
// not tight-loop the refresh.
const MIN_REFRESH_DELAY_MS = 15_000;

const msUntilRefresh = (
  token: string,
  bufferSeconds: number,
): number | null => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (!exp) return null;
    return Math.max(exp * 1000 - Date.now() - bufferSeconds * 1000, 0);
  } catch {
    return null;
  }
};

interface Options {
  /** Called when the session can no longer be kept alive (refresh failed). */
  onExpired?: () => void;
  bufferSeconds?: number;
}

/**
 * Keeps the session alive while the app is open: refreshes the access token
 * shortly BEFORE it expires (using the stored refresh token), so a short-lived
 * `qat` doesn't log the admin out mid-session. Mirrors the QL frontends'
 * proactive keepalive, adapted to Backoffice's localStorage + Bearer model.
 * Renders nothing; no-op when there's no stored token (e.g. on /login).
 */
export const useProactiveTokenRefresh = ({
  onExpired,
  bufferSeconds = DEFAULT_BUFFER_SECONDS,
}: Options = {}): void => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep the latest onExpired without re-arming the effect each render.
  const onExpiredRef = useRef<Options['onExpired']>(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    let cancelled = false;
    let stopped = false;

    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleFrom = (token: string | null) => {
      clear();
      if (cancelled || stopped || !token) return;
      const delay = msUntilRefresh(token, bufferSeconds);
      if (delay === null) return;
      timerRef.current = setTimeout(
        () => void runRefresh(),
        Math.max(delay, MIN_REFRESH_DELAY_MS),
      );
    };

    const runRefresh = async () => {
      if (cancelled || stopped || !getStoredRefreshToken()) return;
      const newToken = await refreshAccessToken();
      if (cancelled) return;
      if (newToken) {
        scheduleFrom(newToken);
      } else {
        // Refresh token expired/revoked — session is dead. Log out once.
        stopped = true;
        onExpiredRef.current?.();
      }
    };

    const onFocus = () => {
      if (cancelled || stopped) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      const token = getStoredToken();
      // Token gone but a refresh token remains → refresh now; else reschedule.
      if (!token && getStoredRefreshToken()) void runRefresh();
      else scheduleFrom(token);
    };

    scheduleFrom(getStoredToken());
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      cancelled = true;
      clear();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [bufferSeconds]);
};
